import { type MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import {
  ProductTree,
} from "@financial-advisory/data-access/api-types";
import { SessionProductTree } from "@financial-advisory/data-access/api-types";
import {
  useLazyGetSessionGuardrailTreesQuery,
  useLazyGetSessionProductTreesQuery,
  useSendMessageMutation,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import { SHARED_SEND_MESSAGE_CACHE_KEY } from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  type ChangedNode,
  type QuestionAnswer,
  type SystemState,
  type TreeValue,
} from "@unlikelyai-magic/utils";
import {
  getDedupedLeafNodes,
  getSystemStateValues,
  getTreeTypeFromId,
  isOutcomeDecisive,
  mergeOldAndNewQuestionAnswers,
  normaliseString,
} from "@unlikelyai-magic/utils";
import { useBranding } from "@targeted-support/ui/contexts";
import { ChatPanel } from "./ChatPanel";
import { SimulateModal } from "./SimulateModal";
import { SystemThinkingPanel } from "./SystemThinkingPanel";
import { AuditLogView } from "./audit-log";
import {
  filterInputGuardrailResults,
  filterOutputGuardrailResults,
} from "./guardrailUtils";
import {
  type ChatMode,
  type ConversationPair,
  type InitialMessage,
  type Persona,
  type ScrollRequest,
  type TurnOutcomeState,
} from "./types";
import { useSimulation } from "./useSimulation";

interface InitialData {
  treeById: Map<string, ProductTree>;
  allNodes: Map<string, ChangedNode>;
  systemState: SystemState;
  staticData: StaticData | null;
  initialMessages: InitialMessage[];
  shouldTerminateEarly?: boolean;
}

interface StaticData {
  initialConversation: ConversationPair[];
  sessionId: string;
  questionAnswers: QuestionAnswer[];
  isComplete: boolean;
  preCalculatedTreeValues: TreeValue[];
}

export const EMPTY_SYSTEM_STATE: SystemState = {
  situations: new Map(),
  segments: new Map(),
  suggestions: new Map(),
};

export const LiveTestPage = ({
  onNavigateToHistory,
  initialData,
  onSimulationRunningChange,
  stopSimulationRef: externalStopRef,
}: {
  onNavigateToHistory: () => void;
  initialData: InitialData;
  onSimulationRunningChange?: (running: boolean) => void;
  stopSimulationRef?: MutableRefObject<(() => void) | null>;
}) => {
  const { tenantId } = useBranding();
  const [conversations, setConversations] = useState<ConversationPair[]>(
    initialData.staticData?.initialConversation ?? [
      {
        id: "conversation-0",
        index: 0,
        userMessage: "",
        systemResponse: "",
        summary: "",
        changedNodes: [],
        outputGuardrailTrees: [],
        inputGuardrailTrees: [],
        sessionVersionId: "",
      },
    ]
  );
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  /**
   * While this is on, the left panel steps to each turn as the platform finishes
   * working it out. Opening an older turn switches it off so a running simulation
   * cannot pull the reader away from what they are reading.
   */
  const [isFollowingLive, setIsFollowingLive] = useState(true);
  const [systemState, setSystemState] = useState<SystemState>(
    initialData.systemState
  );

  const [sessionId, setSessionId] = useState<string>(
    initialData.staticData?.sessionId ?? ""
  );
  const [isComplete, setIsComplete] = useState(
    initialData.staticData?.isComplete ?? false
  );

  const [sendMessageMutation] = useSendMessageMutation({
    fixedCacheKey: SHARED_SEND_MESSAGE_CACHE_KEY,
  });
  const [fetchSessionGuardrailTrees] = useLazyGetSessionGuardrailTreesQuery();
  const [fetchSessionProductTrees] = useLazyGetSessionProductTreesQuery();
  const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>(
    initialData.staticData?.questionAnswers ?? []
  );
  const [isLoading, setIsLoading] = useState(false);
  const orderedSessionProductTrees: SessionProductTree[][] = useMemo(() => {
    return [];
  }, []);
  // Track scroll requests with unique IDs to ensure effects always trigger
  const [chatScrollRequest, setChatScrollRequest] =
    useState<ScrollRequest | null>(null);
  const [showDecisionExplanation, setShowDecisionExplanation] = useState(false);
  const [shouldTerminateEarly, setShouldTerminateEarly] = useState(
    initialData.shouldTerminateEarly ?? false
  );
  const [executeOutputGuardrails, setExecuteOutputGuardrails] = useState(true);
  const [executeInputGuardrails, setExecuteInputGuardrails] = useState(true);
  const shouldTerminateEarlyRef = useRef(shouldTerminateEarly);
  const executeOutputGuardrailsRef = useRef(executeOutputGuardrails);
  const executeInputGuardrailsRef = useRef(executeInputGuardrails);
  executeOutputGuardrailsRef.current = executeOutputGuardrails;
  executeInputGuardrailsRef.current = executeInputGuardrails;
  shouldTerminateEarlyRef.current = shouldTerminateEarly;
  const isFollowingLiveRef = useRef(isFollowingLive);
  isFollowingLiveRef.current = isFollowingLive;
  const selectedIndexRef = useRef(selectedIndex);
  selectedIndexRef.current = selectedIndex;
  const sessionGenerationRef = useRef(0);
  const cancellingRef = useRef(false);
  const isMidTurnRef = useRef(false);
  isMidTurnRef.current = conversations.some(
    (conv) => conv.isLoadingResponse || conv.isUserTyping
  );
  const simulationTurnOffsetRef = useRef(0);
  const stopSimulationRef = useRef<() => void>(() => { /* no-op */ });
  const fetchAndProcessProductTreesRef = useRef<
    (
      sessionVersionId: string,
      allNodes: Map<string, ChangedNode>,
      generation: number,
      targetSystemIndex?: number,
      targetUserIndex?: number
    ) => Promise<void>
  >(() => Promise.resolve());

  const isResumedChat = !!initialData.staticData;
  const [chatMode, setChatMode] = useState<ChatMode>(
    isResumedChat ? "manual" : "idle"
  );
  const [showSimulateModal, setShowSimulateModal] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [simulationInitialMessages, setSimulationInitialMessages] = useState<
    InitialMessage[]
  >([]);

  const handleSimulationAssistantMessage = useCallback(
    (event: {
      content: string;
      session_id: string;
      session_version_id: string;
      is_complete: boolean;
      target_questions: string[];
      turn: number;
    }) => {
      const generation = sessionGenerationRef.current;
      const userMessageIndex = event.turn + simulationTurnOffsetRef.current;
      const systemResponseIndex = event.turn + simulationTurnOffsetRef.current + 1;

      setConversations((prev) =>
        prev.map((conv) =>
          conv.index === userMessageIndex
            ? { ...conv, sessionVersionId: event.session_version_id }
            : conv
        )
      );

      fetchSessionGuardrailTrees({
        sessionVersionId: event.session_version_id,
      }).then(({ data: getNewGuardrailData }) => {
        if (sessionGenerationRef.current !== generation) return;

        const allGuardrailResults = getNewGuardrailData?.data ?? [];
        const outputGuardrailResults =
          filterOutputGuardrailResults(allGuardrailResults);
        const inputGuardrailResults =
          filterInputGuardrailResults(allGuardrailResults);

        const matchingGuardrailResult = outputGuardrailResults.find(
          (r) => r.message?.content === event.content
        );

        const outputGuardrailSequence =
          matchingGuardrailResult &&
          matchingGuardrailResult.sequence_id &&
          matchingGuardrailResult.sequence_number > 1
            ? outputGuardrailResults
                .filter(
                  (r) => r.sequence_id === matchingGuardrailResult.sequence_id
                )
                .sort((a, b) => a.sequence_number - b.sequence_number)
            : matchingGuardrailResult
              ? [matchingGuardrailResult]
              : [];

        setConversations((prev) =>
          prev.map((conv) => {
            if (conv.index === systemResponseIndex) {
              return {
                ...conv,
                systemResponse: event.content,
                isLoadingResponse: false,
                outputGuardrailTrees: outputGuardrailSequence,
                targetQuestions: event.target_questions ?? [],
              };
            } else if (conv.index === userMessageIndex) {
              return { ...conv, inputGuardrailTrees: inputGuardrailResults };
            }
            return conv;
          })
        );

        setIsComplete(event.is_complete);
        setIsLoading(false);

        if (cancellingRef.current) {
          cancellingRef.current = false;
          stopSimulationRef.current();
          setChatMode(event.is_complete ? "complete" : "paused");
        } else if (event.is_complete) {
          setChatMode("complete");
        }
      });

      // The panel steps on once the turn is worked out, so it never shows a
      // half-filled turn and never steps backwards.
      if (isFollowingLiveRef.current) {
        setSelectedIndex(userMessageIndex);
      }

      fetchAndProcessProductTreesRef.current(
        event.session_version_id,
        initialData.allNodes,
        generation,
        systemResponseIndex,
        userMessageIndex
      );
      setSessionId(event.session_id);
    },
    [initialData.allNodes]
  );

  const { startSimulation, stopSimulation } = useSimulation({
    onInitialMessages: (event) => {
      const msgs = event.messages.map((content, i) => ({
        id: `sim-initial-${i}`,
        content,
      }));
      setSimulationInitialMessages(msgs);
    },
    onUserTyping: (event) => {
      setConversations((prev) =>
        prev.map((conv) =>
          conv.index === prev.length - 1
            ? { ...conv, isUserTyping: true }
            : conv
        )
      );
    },
    onUserMessage: (event) => {
      setIsLoading(true);
      let firstTurnIndex: number | null = null;
      setConversations((prev) => {
        const updated = prev.map((conv) =>
          conv.index === prev.length - 1
            ? {
                ...conv,
                userMessage: event.content,
                isUserTyping: false,
                changedNodes: [],
                outcomeStates: undefined,
                isLoadingSystemThinking: true,
              }
            : conv
        );
        const newConversations = [
          ...updated,
          {
            id: `Conversation-${Date.now()}`,
            index: updated.length,
            userMessage: "",
            systemResponse: "",
            summary: "",
            changedNodes: [],
            isLoadingResponse: true,
            isLoadingSystemThinking: true,
            outputGuardrailTrees: [],
            inputGuardrailTrees: [],
            sessionVersionId: "",
            targetQuestions: [],
          },
        ];
        firstTurnIndex = newConversations.length - 2;
        return newConversations;
      });
      // Nothing is selected on the opening turn, so select it and let the turn
      // card show that it is still being worked out.
      if (isFollowingLiveRef.current && selectedIndexRef.current === null) {
        setSelectedIndex(firstTurnIndex);
      }
    },
    onAssistantMessage: handleSimulationAssistantMessage,
    onSimulationComplete: () => {
      cancellingRef.current = false;
      setIsLoading(false);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.isUserTyping ? { ...conv, isUserTyping: false } : conv
        )
      );
      setChatMode((prev) =>
        prev === "simulating" || prev === "cancelling" ? "complete" : prev
      );
    },
    onError: (error) => {
      console.error("Simulation error:", error);
      cancellingRef.current = false;
      setIsLoading(false);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.isUserTyping ? { ...conv, isUserTyping: false } : conv
        )
      );
      setChatMode("paused");
    },
    onStreamEnd: () => {
      setIsLoading(false);
      if (cancellingRef.current) {
        cancellingRef.current = false;
        setChatMode("paused");
      }
    },
  });

  stopSimulationRef.current = stopSimulation;

  const isSimulationActive = chatMode === "simulating" || chatMode === "cancelling";

  useEffect(() => {
    if (!isSimulationActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isSimulationActive]);

  useEffect(() => {
    onSimulationRunningChange?.(isSimulationActive);
  }, [isSimulationActive, onSimulationRunningChange]);

  const handleNewChat = useCallback(() => {
    cancellingRef.current = false;
    simulationTurnOffsetRef.current = 0;
    stopSimulation();
    sessionGenerationRef.current += 1;
    setConversations([
      {
        id: "conversation-0",
        index: 0,
        userMessage: "",
        systemResponse: "",
        summary: "",
        changedNodes: [],
        outputGuardrailTrees: [],
        inputGuardrailTrees: [],
        sessionVersionId: "",
      },
    ]);
    setSelectedIndex(null);
    setIsFollowingLive(true);
    setSystemState(
      getSystemStateValues(Array.from(initialData.treeById.values()), [])
    );
    setSessionId("");
    setIsComplete(false);
    setQuestionAnswers([]);
    setIsLoading(false);
    setChatScrollRequest(null);
    setShowDecisionExplanation(false);
    setChatMode("idle");
    setSelectedPersona(null);
    setSimulationInitialMessages([]);
    orderedSessionProductTrees.length = 0;
  }, [initialData.treeById, orderedSessionProductTrees, stopSimulation]);

  const handleSelectMessage = useCallback((index: number) => {
    setSelectedIndex(index);
    setIsFollowingLive(false);
    setChatScrollRequest(null);
  }, []);

  /**
   * Puts the panel back on the latest turn the platform has finished, and brings
   * the chat back to the bottom so both columns show the same moment again.
   */
  const handleFollowLive = useCallback(() => {
    setIsFollowingLive(true);
    const latestEvaluated = [...conversations]
      .reverse()
      .find(
        (conv) => conv.userMessage.trim() !== "" && !conv.isLoadingSystemThinking
      );
    if (latestEvaluated) {
      setSelectedIndex(latestEvaluated.index);
    }
    setChatScrollRequest({
      index: conversations.length - 1,
      requestId: Date.now(),
    });
  }, [conversations]);

  /**
   * Clicking an answered question scrolls the chat to the turn that produced it.
   */
  const handleSelectKnowledge = useCallback(
    (question: string) => {
      const normalised = question.toLowerCase().trim();
      for (let i = conversations.length - 1; i >= 0; i--) {
        const conv = conversations[i];
        const match = conv.changedNodes?.some(
          (node) => node.question.toLowerCase().trim() === normalised
        );
        if (match) {
          setChatScrollRequest({ index: conv.index, requestId: Date.now() });
          setSelectedIndex(conv.index);
          setIsFollowingLive(false);
          return;
        }
      }
    },
    [conversations]
  );

  const _handleSendUserMessage = async (
    text: string
  ): Promise<
    [string, string, string, boolean, string[] | undefined] | undefined
  > => {
    const normalisedText = text.trim();
    if (!normalisedText) return;

    try {
      const result = await sendMessageMutation({
        message: normalisedText,
        session_id: sessionId,
        should_terminate_early_on_recommendation:
          shouldTerminateEarlyRef.current,
        execute_output_guardrails: executeOutputGuardrailsRef.current,
        execute_input_guardrails: executeInputGuardrailsRef.current,
      });

      if ("data" in result && result.data) {
        const data: any = result.data;
        const {
          message,
          session_id,
          session_version_id,
          is_complete,
          target_questions,
        } = data;

        return [
          message,
          session_version_id,
          session_id,
          is_complete ?? false,
          target_questions,
        ];
      }
    } catch (error) {
      console.error("Failed to send message to backend", error);
      return [
        "I'm sorry, I couldn't process your request. Please try again later.",
        "",
        "",
        false,
        [],
      ];
    }
  };

  const processSystemResponse = useCallback(
    async (message: string) => {
      const generation = sessionGenerationRef.current;
      const result = await _handleSendUserMessage(message);
      if (sessionGenerationRef.current !== generation) return;
      if (!result || !result[0] || result[0].trim() === "") {
        return;
      }
      setIsLoading(false);
      const [
        content,
        session_version_id,
        session_id,
        is_complete,
        target_questions,
      ] = result;
      // Now we have the new session version id, we need to update the last conversation item with the new session version id
      setConversations((prev) =>
        prev.map((conv) =>
          conv.index === prev.length - 2
            ? { ...conv, sessionVersionId: session_version_id }
            : conv
        )
      );

      const { data: getNewGuardrailData } = await fetchSessionGuardrailTrees({
        sessionVersionId: session_version_id,
      });
      if (sessionGenerationRef.current !== generation) return;

      // Separate guardrail results by type (INPUT vs OUTPUT)
      const allGuardrailResults = getNewGuardrailData?.data ?? [];
      const outputGuardrailResults =
        filterOutputGuardrailResults(allGuardrailResults);
      const inputGuardrailResults =
        filterInputGuardrailResults(allGuardrailResults);

      // Find the matching output guardrail result by comparing message content
      const matchingGuardrailResult = outputGuardrailResults.find(
        (result) => result.message?.content === content
      );

      // If the message was rephrased, include the full guardrail sequence for this message
      const outputGuardrailSequence =
        matchingGuardrailResult &&
        matchingGuardrailResult.sequence_id &&
        matchingGuardrailResult.sequence_number > 1
          ? (outputGuardrailResults
              .filter(
                (result) =>
                  result.sequence_id === matchingGuardrailResult.sequence_id
              )
              .sort((a, b) => a.sequence_number - b.sequence_number) ?? [])
          : matchingGuardrailResult
            ? [matchingGuardrailResult]
            : [];

      // Add a new conversation item with the bot response and guardrail data
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.index === prev.length - 1) {
            // Current conversation with agent response - attach output guardrails
            return {
              ...conv,
              systemResponse: content,
              isLoadingResponse: false,
              outputGuardrailTrees: outputGuardrailSequence,
              targetQuestions: target_questions ?? [],
            };
          } else if (conv.index === prev.length - 2) {
            // Previous conversation with user message - attach input guardrails
            return {
              ...conv,
              inputGuardrailTrees: inputGuardrailResults,
            };
          }
          return conv;
        })
      );
      // The user message went onto the pair that was last when the send started.
      if (isFollowingLiveRef.current) {
        setSelectedIndex(conversations.length - 1);
      }
      fetchAndProcessProductTrees(
        session_version_id,
        initialData.allNodes,
        generation
      );
      setSessionId(session_id);
      setIsComplete(is_complete);
    },
    [initialData.allNodes, conversations, setSelectedIndex]
  );

  const fetchAndProcessProductTrees = useCallback(
    async (
      sessionVersionId: string,
      allNodes: Map<string, ChangedNode>,
      generation: number,
      targetSystemIndex?: number,
      targetUserIndex?: number
    ) => {
      const { data: sessionProductTrees } = await fetchSessionProductTrees({
        sessionVersionId: sessionVersionId,
      });
      if (sessionGenerationRef.current !== generation) return;
      const newSessionProductTrees = sessionProductTrees?.data ?? [];
      const contradictingQuestions =
        sessionProductTrees?.contradicting_questions ?? [];
      let changedAnswers: QuestionAnswer[] = [];
      let allQuestionAnswers: QuestionAnswer[] = [];

      const existingQuestionAnswers = [...questionAnswers];

      if (orderedSessionProductTrees.length === 0) {
        const newLeaves = getDedupedLeafNodes(
          newSessionProductTrees.flatMap((tree) => tree.leaf_nodes)
        );
        allQuestionAnswers = Array.from(newLeaves.values());
        const merged = mergeOldAndNewQuestionAnswers(
          existingQuestionAnswers,
          allQuestionAnswers
        );
        setQuestionAnswers(merged);
        const existingAnswerMap = new Map(
          existingQuestionAnswers.map((qa) => [qa.question, qa])
        );
        changedAnswers = Array.from(newLeaves.values()).filter(
          (node) =>
            isOutcomeDecisive(node.value) &&
            node.value !== existingAnswerMap.get(node.question)?.value
        );
      } else {
        const lastSessionProductTrees =
          orderedSessionProductTrees[orderedSessionProductTrees.length - 1];
        const previousLeaves = getDedupedLeafNodes(
          lastSessionProductTrees.flatMap((tree) => tree.leaf_nodes)
        );
        const newLeaves = getDedupedLeafNodes(
          newSessionProductTrees.flatMap((tree) => tree.leaf_nodes)
        );
        allQuestionAnswers = Array.from(newLeaves.values());

        const merged = mergeOldAndNewQuestionAnswers(
          existingQuestionAnswers,
          allQuestionAnswers
        );
        setQuestionAnswers(merged);
        changedAnswers = Array.from(newLeaves.values()).filter(
          (node) =>
            isOutcomeDecisive(node.value) &&
            node.value !== previousLeaves.get(node.question)?.value
        );
      }

      const newSystemState = getSystemStateValues(
        Array.from(initialData.treeById.values()),
        allQuestionAnswers
      );
      orderedSessionProductTrees.push(sessionProductTrees?.data ?? []);

      const changedNodes: ChangedNode[] = changedAnswers.map((answer) => {
        const changedNode = allNodes.get(normaliseString(answer.question));
        if (!changedNode) {
          console.error(
            `Changed node not found for question: ${answer.question}`
          );
        }
        return {
          ...changedNode!,
          value: answer.value,
          reasoning: answer.reasoning,
        };
      });

      // Extract outcome states from session product trees
      const outcomeStates: TurnOutcomeState[] = newSessionProductTrees.map(
        (spt) => {
          const productTree = initialData.treeById.get(spt.product_tree_id);
          return {
            productTreeId: spt.product_tree_id,
            name: productTree?.name ?? spt.product_tree_id,
            type: getTreeTypeFromId(spt.product_tree_id),
            outcome: spt.outcome,
          };
        }
      );

      setConversations((prev) => {
        const sysIdx = targetSystemIndex ?? prev.length - 1;
        const usrIdx = targetUserIndex ?? prev.length - 2;
        return prev.map((conv) => {
          if (conv.index === sysIdx || conv.index === usrIdx) {
            return {
              ...conv,
              changedNodes: changedNodes,
              isLoadingSystemThinking: false,
              outcomeStates,
              contradictingQuestions,
            };
          }
          return conv;
        });
      });
      setSystemState(newSystemState);
    },
    [
      initialData.treeById,
      setSystemState,
      setIsLoading,
      setConversations,
      questionAnswers,
    ]
  );
  fetchAndProcessProductTreesRef.current = fetchAndProcessProductTrees;

  const handleSendMessage = useCallback(
    (message: string) => {
      setChatMode("manual");
      setIsFollowingLive(true);
      setIsLoading(true);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.index === prev.length - 1
            ? {
                ...conv,
                userMessage: message,
                changedNodes: [],
                outcomeStates: undefined,
                isLoadingSystemThinking: true,
              }
            : conv
        )
      );
      setConversations((prev) => [
        ...prev,
        {
          id: `Conversation-${Date.now()}`,
          index: prev.length,
          userMessage: "",
          systemResponse: "",
          summary: "",
          changedNodes: [],
          isLoadingResponse: true,
          outputGuardrailTrees: [],
          inputGuardrailTrees: [],
          sessionVersionId: "",
          targetQuestions: [],
        },
      ]);
      if (conversations.length == 1) {
        setSelectedIndex(conversations.length - 1);
      }
      processSystemResponse(message);
    },
    [setIsLoading, conversations, setSelectedIndex]
  );

  const handleStartSimulation = useCallback(() => {
    setShowSimulateModal(true);
  }, []);

  const handleSelectPersona = useCallback(
    (persona: Persona) => {
      simulationTurnOffsetRef.current = 0;
      setSelectedPersona(persona);
      setShowSimulateModal(false);
      setIsFollowingLive(true);
      setChatMode("simulating");
      startSimulation({
        persona_detail: persona.personaDetail,
        situation_summary: persona.situationSummary,
        should_terminate_early: shouldTerminateEarlyRef.current,
        execute_output_guardrails: executeOutputGuardrailsRef.current,
        execute_input_guardrails: executeInputGuardrailsRef.current,
      });
    },
    [startSimulation]
  );

  const handleStopSimulation = useCallback(() => {
    if (isMidTurnRef.current) {
      cancellingRef.current = true;
      setChatMode("cancelling");
    } else {
      stopSimulation();
      setIsLoading(false);
      setChatMode("paused");
    }
  }, [stopSimulation]);

  const handleForceStopSimulation = useCallback(() => {
    cancellingRef.current = false;
    stopSimulation();
    setIsLoading(false);
  }, [stopSimulation]);

  useEffect(() => {
    if (externalStopRef) {
      externalStopRef.current = handleForceStopSimulation;
    }
    return () => {
      if (externalStopRef) {
        externalStopRef.current = null;
      }
    };
  }, [handleForceStopSimulation, externalStopRef]);

  const handleContinueSimulation = useCallback(() => {
    if (!selectedPersona) return;

    const history: { role: string; content: string }[] = [];
    const initialMsgs =
      simulationInitialMessages.length > 0
        ? simulationInitialMessages
        : initialData.initialMessages;
    for (const msg of initialMsgs) {
      history.push({ role: "assistant", content: msg.content });
    }
    for (const conv of conversations) {
      if (conv.userMessage.trim()) {
        history.push({ role: "user", content: conv.userMessage });
      }
      if (conv.systemResponse.trim()) {
        history.push({ role: "assistant", content: conv.systemResponse });
      }
    }

    simulationTurnOffsetRef.current = conversations.length - 1;
    setIsFollowingLive(true);
    setChatMode("simulating");
    startSimulation({
      persona_detail: selectedPersona.personaDetail,
      situation_summary: selectedPersona.situationSummary,
      session_id: sessionId || null,
      should_terminate_early: shouldTerminateEarlyRef.current,
      execute_output_guardrails: executeOutputGuardrailsRef.current,
      execute_input_guardrails: executeInputGuardrailsRef.current,
      conversation_history: history,
    });
  }, [
    selectedPersona,
    sessionId,
    startSimulation,
    conversations,
    simulationInitialMessages,
    initialData.initialMessages,
  ]);

  /**
   * True while the platform is still working a turn out, so the panel is showing
   * the turn before it on purpose.
   */
  const isEvaluatingTurn = conversations.some(
    (conv) => conv.isLoadingSystemThinking || conv.isLoadingResponse
  );
  const isLive = isSimulationActive || isLoading || isEvaluatingTurn;

  const handleSwitchToManual = useCallback(() => {
    cancellingRef.current = false;
    stopSimulation();
    setIsLoading(false);
    setChatMode("manual");
  }, [stopSimulation]);

  return (
    <div className="relative flex h-full min-h-0">
      {/* Content layer */}
      <div className="relative flex h-full min-h-0 w-full">
        {/* Left column - what the platform is doing */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-brand-background p-4 pr-0">
          <SystemThinkingPanel
            productTreesById={initialData.treeById}
            questionAnswers={questionAnswers}
            conversations={conversations}
            selectedIndex={selectedIndex}
            systemState={systemState}
            allNodes={initialData.allNodes}
            onSelectKnowledge={handleSelectKnowledge}
            onSelectTurn={handleSelectMessage}
            isFollowingLive={isFollowingLive}
            isEvaluatingTurn={isEvaluatingTurn}
            isLive={isLive}
            onFollowLive={handleFollowLive}
          />
        </div>

        {/* Right column - the customer's chat */}
        <div className="flex w-[620px] flex-none flex-col overflow-hidden p-4">
          <ChatPanel
            initialMessages={
              simulationInitialMessages.length > 0
                ? simulationInitialMessages
                : initialData.initialMessages
            }
            conversations={conversations}
            selectedIndex={selectedIndex}
            onSelectMessage={handleSelectMessage}
            scrollRequest={chatScrollRequest}
            autoScrollToLatest={isFollowingLive}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            isComplete={isComplete}
            onNewChat={handleNewChat}
            sessionId={sessionId}
            shouldTerminateEarly={shouldTerminateEarly}
            onSetShouldTerminateEarly={setShouldTerminateEarly}
            executeOutputGuardrails={executeOutputGuardrails}
            onSetExecuteOutputGuardrails={setExecuteOutputGuardrails}
            executeInputGuardrails={executeInputGuardrails}
            onSetExecuteInputGuardrails={setExecuteInputGuardrails}
            chatMode={chatMode}
            onStartSimulation={handleStartSimulation}
            onStopSimulation={handleStopSimulation}
            onContinueSimulation={handleContinueSimulation}
            onSwitchToManual={handleSwitchToManual}
            onShowAuditLog={() => setShowDecisionExplanation(true)}
          />
        </div>
      </div>

      {/* Simulate Modal */}
      {showSimulateModal && (
        <SimulateModal
          onClose={() => setShowSimulateModal(false)}
          onSelectPersona={handleSelectPersona}
          tenantId={tenantId}
        />
      )}

      {/* Audit Log Modal */}
      {showDecisionExplanation && (
        <AuditLogView
          initialMessages={initialData.initialMessages}
          conversations={conversations}
          productTreesById={initialData.treeById}
          allNodes={initialData.allNodes}
          systemState={systemState}
          sessionId={sessionId}
          isComplete={isComplete}
          onClose={() => setShowDecisionExplanation(false)}
        />
      )}
    </div>
  );
};
